import type { Root } from 'mdast';
import type { Plugin } from 'unified';

import { visit } from 'unist-util-visit';

/**
 * Remark plugin to detect lists that contain only images
 * and add data attributes for gallery styling.
 *
 * Transforms lists like:
 * - ![Alt text](image1.png)
 * - ![Alt text](image2.png)
 * - ![Alt text](image3.png)
 *
 * Into lists with data-gallery attribute for grid rendering.
 */
export const remarkGallery: Plugin<[], Root> = () => {
  return (tree) => {
    visit(tree, 'list', (node) => {
      // Check if all list items contain only a single image
      const isImageGallery = node.children.every((listItem) => {
        // A list item should have exactly one child (a paragraph)
        if (listItem.children.length !== 1) {
          return false;
        }

        const firstChild = listItem.children[0];

        // The child should be a paragraph
        if (firstChild.type !== 'paragraph') {
          return false;
        }

        // The paragraph should contain exactly one image
        if (firstChild.children.length !== 1) {
          return false;
        }

        const paragraphChild = firstChild.children[0];

        return paragraphChild.type === 'image';
      });

      if (isImageGallery && node.children.length > 0) {
        // Add data attribute to the node
        node.data = {
          ...node.data,
          hProperties: {
            ...(node.data?.hProperties as Record<string, unknown>),
            'data-gallery': 'true',
          },
        };
      }
    });
  };
};
